import { getJSON } from 'common/services/api'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useMemo } from 'react'
import { useQueries, useQuery } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import type { RotaEventInstance } from '../types'
import { YEAR_FIRST_DATE_FORMAT } from '../utils.datetime'
import { INSTANCES_QUERY_KEY, INSTANCES_URL } from './queryConstants'

dayjs.extend(isoWeek)

const RECURSIVE_LIMIT = 100
const FIELDS = [
  '*',
  'carer.picture',
  'resident.name',
  'resident.picture',
  'resident.status'
]

type UseRecursiveInstancesListReturn = {
  instances: RotaEventInstance[]
  isLoading: boolean
  metadata: { unassignedHours: number }
  errorCode: number
}

type UseRecursiveInstancesListProps = {
  selectedDate?: Date
  intervalType?: 'daily' | 'weekly'
  otherFilterParams?: Record<string, any>
  totalEvents?: number
  recursionEnabled?: boolean
}

const useRecursiveInstancesList = ({
  selectedDate,
  intervalType,
  otherFilterParams = {},
  totalEvents,
  recursionEnabled = false
}: UseRecursiveInstancesListProps = {}): UseRecursiveInstancesListReturn => {
  const hasTotalEventsDefined = totalEvents !== undefined

  const isRecursiveFetch =
    recursionEnabled && hasTotalEventsDefined && totalEvents >= RECURSIVE_LIMIT

  const date =
    intervalType === 'weekly'
      ? dayjs(selectedDate).startOf('isoWeek').format(YEAR_FIRST_DATE_FORMAT)
      : dayjs(selectedDate).format(YEAR_FIRST_DATE_FORMAT)

  const params = getQueryParams({
    date,
    intervalType,
    fields: FIELDS,
    ...otherFilterParams
  })

  const { isLoading, data: instances } = useQuery(
    [INSTANCES_QUERY_KEY, params, otherFilterParams.siteShift],
    () =>
      getJSON({
        url: `${INSTANCES_URL}?${params}`,
        isRota: true,
        customErrorHandling: true
      }),
    {
      enabled: !isRecursiveFetch && !!selectedDate && !!intervalType,
      retry: false
    }
  )

  // Recursive logic
  const loops = hasTotalEventsDefined
    ? Math.ceil(totalEvents / RECURSIVE_LIMIT)
    : 1

  const queries = Array.from({ length: loops }).map((_, index) => {
    const recursiveParams = getQueryParams({
      date,
      intervalType,
      fields: FIELDS,
      ...otherFilterParams,
      page: index + 1,
      limit: RECURSIVE_LIMIT
    })

    return {
      queryKey: [
        INSTANCES_QUERY_KEY,
        recursiveParams,
        otherFilterParams.siteShift
      ],
      queryFn: () =>
        getJSON({
          url: `${INSTANCES_URL}?${recursiveParams}`,
          isRota: true
        }),
      enabled:
        hasTotalEventsDefined &&
        !!selectedDate &&
        !!intervalType &&
        isRecursiveFetch
    }
  })

  const recursiveResults = useQueries(queries)

  const recursiveData = useMemo(() => {
    const data = recursiveResults.flatMap((result) => result?.data?.data || [])

    const onlyDefinedData = data.filter((item) => item !== undefined)

    return onlyDefinedData
  }, [recursiveResults])

  const singleQueryData = instances?.data || []

  const finalInstances = !isRecursiveFetch ? singleQueryData : recursiveData

  const recursiveIsLoading = useMemo(() => {
    if (!isRecursiveFetch || !recursiveResults.length) return false

    return recursiveResults.some((result) => result.isLoading)
  }, [isRecursiveFetch, recursiveResults])

  const recursiveError = useMemo(() => {
    if (!isRecursiveFetch || !recursiveResults.length) return null

    const errorResult = recursiveResults.find((result) => result.error)
    return errorResult?.error || null
  }, [isRecursiveFetch, recursiveResults])

  return {
    instances: finalInstances,
    isLoading: !isRecursiveFetch ? !!isLoading : recursiveIsLoading,
    metadata: instances?.metadata,
    errorCode: instances?.statusCode || (recursiveError as any)?.statusCode
  }
}

export default useRecursiveInstancesList
